import { logger } from '@dealerportal/core';
import { toCicLocale } from '@dealerportal/i18n';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

const SESSION_ID = 'cicSessionId';

const fetchCatalogVersion = async (catalogId: string) => {
  const response = await globalThis.CiCAPI.content.v2.getCatalog(catalogId);
  const catalog = response.result;
  if (!catalog) {
    logger.error(`Catalog not found with catalogId ${catalogId}`);
    return;
  }
  const catalogVersionId = catalog?.catalogVersions[0]?.id ?? '';
  return catalogVersionId;
};

const CiCScriptId = 'cic-js-script';

type Options = {
  loadScript: boolean;
  /**
   * Site locale (`en` | `pt` | `es`). Mapped to the CiC locale that decides
   * the language of facet and value labels returned by the CiC client.
   */
  locale?: string;
};

type UseCiCAPIResult = {
  loaded: boolean;
  fetchCatalogVersion: (catalogId: string) => Promise<string | undefined>;
};

const configOverrides: IConfig = {
  version: 0,
  contentPlatform: {
    nbWorkers: 4,
    useCatalogCrawler: false,
    useCatalogCrawlerParallelDownloads: false,
    useDesignServices: false,
    skipSearchCatalogDependenciesAttributes: true,
  },
  appInsight: {
    enabled: false,
  },
};

const options: ICiCAPIInitOptions = {
  apiSrcUrl: process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL ?? '',
  configOverrides,
  partnership: undefined,
  crawlPartnershipContent: false,
};

// The CiC client bootstrap is a document-wide singleton: loading the remote
// script re-downloads the immutable, content-hashed runtime chunk plus the
// whole manifest/config/catalog set, so it must happen once per document no
// matter how many times a consumer of this hook mounts (see #3164).
let bootstrap: Promise<void> | null = null;

const readSessionId = (): string => {
  let storage: Storage | undefined;
  try {
    storage = globalThis.localStorage;
  } catch {
    // Access throws in browsers with storage blocked entirely.
  }
  if (!storage) {
    logger.warn('Local storage is not available');
    return uuid();
  }
  const existing = storage.getItem(SESSION_ID);
  if (existing) return existing;
  const sessionId = uuid();
  storage.setItem(SESSION_ID, sessionId);
  return sessionId;
};

const initCiCAPI = async (locale: string | undefined): Promise<void> => {
  const sessionId = readSessionId();
  const cicLocale = toCicLocale(locale);
  logger.debug(
    'Initializing CiC API with session ID:',
    sessionId,
    'locale:',
    cicLocale
  );
  await globalThis.CiCAPI.init({
    ...options,
    // `contentPlatform.locale` selects the CiC dictionary used to label
    // facets and facet values; it defaults to en-US when unset.
    configOverrides: {
      ...configOverrides,
      contentPlatform: {
        ...configOverrides.contentPlatform,
        locale: cicLocale,
      } as IConfig['contentPlatform'],
    },
    sessionContext: {
      // Generate a unique ID for each session
      id: sessionId,
      name: 'DealerPortal',
      version: '1.0.0',
    },
  });
};

const startBootstrap = (locale: string | undefined): Promise<void> => {
  if (document.getElementById(CiCScriptId)) {
    logger.debug('CiC API script is already loaded');
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = options.apiSrcUrl;
    script.async = true;
    script.id = CiCScriptId;

    script.onload = async () => {
      logger.debug('CiC API script loaded');
      try {
        await initCiCAPI(locale);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    script.onerror = () => {
      reject(
        new Error(`Failed to load CiC API script from ${options.apiSrcUrl}`)
      );
    };

    logger.debug('Appending CiC API script to document body');
    document.body.appendChild(script);
  });
};

export const useCiCAPI = (opts: Options): UseCiCAPIResult => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    logger.debug('useCiCAPI hook mounted, starting initialization');
    if (!opts.loadScript) {
      logger.debug('CiC API script loading is disabled via options');
      return;
    }

    if (process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL == null) {
      throw new Error(
        'NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL environment variable is not set'
      );
    }

    // The script element and the initialized CiCAPI global outlive this
    // component on purpose; tearing them down would force the next mount to
    // re-run the whole bootstrap over the network. `CiCAPI.init` is
    // document-wide, so the locale of the first requester is the one that
    // applies until the next document navigation.
    bootstrap ??= startBootstrap(opts.locale);

    let cancelled = false;
    bootstrap
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      .catch((error) => {
        // The CiC client script dynamically imports its own chunks at runtime;
        // a failed chunk fetch (stale hash, network blip, remote CDN issue)
        // must not surface as an unhandled promise rejection. Degrade to the
        // catalog UI simply not loading.
        logger.withError(error).error('Failed to initialize CiC API');
      });

    return () => {
      cancelled = true;
    };
    // `loadScript` starts false on pages that lazily require CiC
    // (`useRequireCiC`), so the effect must re-run when it flips to true.
  }, [opts.loadScript]);

  return { loaded, fetchCatalogVersion };
};
